import { IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { PerformanceKpi } from "@/components/shared/performance/performance";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
} from "@/components/ui/select";
import type { DashboardPerformanceMetric } from "@/lib/dashboard-sales";
import { useFormatters } from "@/lib/format";
import type { VendorMetricAvailability } from "@/lib/vendor-metric-availability";
import { DASHBOARD_METRIC_GROUPS, formatMetricValue } from "./kpi-strip-metrics";

const RESET = "reset";

export interface KpiMetricSelectProps {
  /** Metric this card currently shows. */
  value: DashboardPerformanceMetric;
  /** Every card's metric, in strip order; metrics on other cards are disabled. */
  slots: readonly DashboardPerformanceMetric[];
  kpis: ReadonlyMap<DashboardPerformanceMetric, PerformanceKpi<DashboardPerformanceMetric>>;
  getLabel: (metric: DashboardPerformanceMetric) => string;
  onValueChange: (metric: DashboardPerformanceMetric) => void;
  /** Restores the default strip; omitted while the strip already is the default. */
  onReset?: () => void;
  /** Dot before the label, matching the metric's Performance chart color. */
  dotColor: string;
  /**
   * Scope availability of each metric (#679): a metric Vendor Central can supply
   * later lists as "coming soon"; one a vendor never gets is left out of the menu.
   */
  availability?: (metric: DashboardPerformanceMetric) => VendorMetricAvailability;
}

/**
 * Metric select on the KPI card label. The menu lists all metrics by group with the
 * values already loaded for the dashboard (no extra request); metrics shown on
 * another card are disabled and name that card so the strip never repeats a metric.
 */
export function KpiMetricSelect({
  value,
  slots,
  kpis,
  getLabel,
  onValueChange,
  onReset,
  dotColor,
  availability = () => "available",
}: KpiMetricSelectProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const { t: tCommon } = useTranslation("common");
  const { formatNumber } = useFormatters();

  return (
    <Select<DashboardPerformanceMetric | typeof RESET>
      value={value}
      onValueChange={(next) => {
        if (next === RESET) onReset?.();
        else if (next && next !== value) onValueChange(next);
      }}
    >
      <SelectTrigger
        size="xs"
        variant="ghost"
        aria-label={t("overview.kpi.changeMetric", { metric: getLabel(value) })}
        className="max-w-full text-muted-foreground hover:text-ink aria-expanded:text-ink"
      >
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full"
          style={{ background: dotColor }}
        />
        <span className="truncate">{getLabel(value)}</span>
      </SelectTrigger>
      <SelectContent size="compact" align="start" className="w-52">
        {DASHBOARD_METRIC_GROUPS.map(({ group, metrics }) => {
          const listed = metrics.filter((metric) => availability(metric) !== "never");
          if (listed.length === 0) return null;
          return (
            <SelectGroup key={group}>
              <SelectLabel>{t(`overview.kpi.groups.${group}`)}</SelectLabel>
              {listed.map((metric) => {
                const otherCard = slots.indexOf(metric);
                const takenElsewhere = otherCard !== -1 && metric !== value;
                const kpi = kpis.get(metric);
                return (
                  <SelectItem key={metric} value={metric} disabled={takenElsewhere}>
                    <span className="min-w-0 grow truncate">{getLabel(metric)}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {takenElsewhere
                        ? t("overview.kpi.onCard", { card: otherCard + 1 })
                        : availability(metric) === "pending_vendor_central"
                          ? tCommon("vendorData.pendingShort")
                          : kpi?.loading
                            ? "…"
                            : formatMetricValue(metric, kpi?.value, kpi?.currency, {
                                formatNumber,
                              })}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          );
        })}
        {onReset ? (
          <>
            <SelectSeparator />
            <SelectItem value={RESET} className="text-text-secondary">
              <IconRefresh aria-hidden className="size-3.5" />
              {t("overview.kpi.resetDefault")}
            </SelectItem>
          </>
        ) : null}
      </SelectContent>
    </Select>
  );
}
